import { useMutation } from 'react-query'

import agent from 'agent'
import useAuth from 'hooks/use-auth'
import { type RunTestEndpointResponse } from 'queries/external-integrations/types'
import {
  getEndpointWithParams,
  throwIfEmptyResponse
} from 'queries/queries-utils'
import { statusChecksMutationKeys } from 'queries/status-checks/query-keys'
import {
  throwIfBadResponseForRunTestEndpoint,
  throwIfFailedPredicateForRunTestEndpoint
} from 'queries/status-checks/query-utils'
import {
  type RunTestEvaluationParams,
  type UseStatusCheckRunTestEvaluationOptions,
  type UseStatusCheckRunTestEvaluationReturnType
} from 'queries/status-checks/types'

function runStatusCheckTestEvaluation(
  mutationKey: ReturnType<typeof statusChecksMutationKeys.runTestEvaluation>,
  signal?: AbortSignal
) {
  return async function (requestBody: RunTestEvaluationParams) {
    const endpointWithParams = getEndpointWithParams(...mutationKey)
    const response = await agent.post<RunTestEndpointResponse>(
      endpointWithParams,
      requestBody,
      { signal }
    )

    throwIfEmptyResponse(response)
    throwIfBadResponseForRunTestEndpoint(response)
    throwIfFailedPredicateForRunTestEndpoint(response)

    return response.data
  }
}

// Export function type signatures
export function useStatusCheckRunTestEvaluation(
  signal?: AbortSignal,
  options?: UseStatusCheckRunTestEvaluationOptions
): UseStatusCheckRunTestEvaluationReturnType

export function useStatusCheckRunTestEvaluation(
  options?: UseStatusCheckRunTestEvaluationOptions
): UseStatusCheckRunTestEvaluationReturnType

/**
 * Submits a POST request which validates the evaluation configuration of a Status Check or Authentication.
 */
export function useStatusCheckRunTestEvaluation(
  ...args:
    | [AbortSignal?, UseStatusCheckRunTestEvaluationOptions?]
    | [UseStatusCheckRunTestEvaluationOptions?]
) {
  const { currentTeamId } = useAuth()
  const mutationKey = statusChecksMutationKeys.runTestEvaluation(currentTeamId)
  const [signalOrOptions, maybeOptions] = args
  const hasSignal = signalOrOptions instanceof AbortSignal

  return useMutation({
    ...(maybeOptions ?? signalOrOptions),
    mutationFn: runStatusCheckTestEvaluation(
      mutationKey,
      hasSignal ? signalOrOptions : undefined
    ),
    mutationKey
  })
}
